/* eslint-disable max-params */
import type { RouteLocationNormalized } from 'vue-router'

import type { RoutesWithThreadIdParam } from './types'

import { navigationErrorHandler } from '~/contexts/dm/router/navigation-error-handler'
import { ROUTE_DM } from '~/contexts/dm/router/routes'

import { ERROR_LEGACY_DM_KO } from '~/config/conversation'

import { Navigation404Error, NavigationRedirectError } from '~/router/errors'
import { hasParamsChanged } from '~/router/utils/has-params-changed'

import type { NavigationGuard } from '~/typings/vue-router'

const RTCTimeoutErrorCode = 'request timeout'

/**
 * Check the given community exists
 * @param containerUri the community
 */
// eslint-disable-next-line max-statements, complexity
async function checkConversationExists(containerUri: string) {
  try {
    const { useConversationsStore } = await import('~/entities/container/conversation/stores/conversations')

    const storeConversations = useConversationsStore()
    await storeConversations.fetchConversation(containerUri)
  }
  catch (error: unknown) {
    if (error instanceof Error && error.message === ERROR_LEGACY_DM_KO) {
      throw new NavigationRedirectError('Conversation migrated to RTC', {
        name: ROUTE_DM.URI_HOME,
        // @ts-expect-error error.cause is not typed
        params: { thread_id: error.cause.rtcId },
      })
    }

    // if the error is not just a websocket timeout error, that means this conversation does not exist..
    else if (!(error instanceof Error) || (error.message !== RTCTimeoutErrorCode)) { 
      throw new Navigation404Error('This conversation doesn\'t exist', { name: ROUTE_DM.HOME, replace: true }) }
  }
}

export const navigationGuardDM: NavigationGuard<ROUTE_DM> = (to, from, next, isMounted, push) => {
  return Promise.resolve()
    .then(async () => {
      if (hasParamsChanged(to, from, 'thread_id')) {
        const _to = to as RouteLocationNormalized<RoutesWithThreadIdParam>
        if (!unref(isMounted) && _to.params.thread_id.includes(':rt:')) {
          /**
           * With RTConv, we can't fetch the conversation until the Vue App is mounted,
           * because we need the WS which is instanciated by TopFrame which is instanciated by the Vue app...
           *
           * This situation MUST BE AVOIDED AS MUCH AS POSSIBLE !!!
           */

          checkConversationExists(_to.params.thread_id)
            .catch((error) => {
              navigationErrorHandler(error, to, from)
              push(error.to)
            })
        }
        else {
          // app is mounted or conversation is not RTConv
          return checkConversationExists(_to.params.thread_id)
        }
      }
    })
    .then(() => next())
    .catch(error => next(error))
}
